import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi, setCsrfToken, setUnauthorizedHandler, ApiError } from '../api/client.js';
import { AuthContext, type AuthState } from './auth-state.js';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);

  const applySession = (session: { authenticated: boolean; username?: string; csrfToken?: string }) => {
    setAuthenticated(session.authenticated);
    setUsername(session.username ?? null);
    setCsrfToken(session.csrfToken ?? null);
  };

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthenticated(false);
      setUsername(null);
      setCsrfToken(null);
    });
    void authApi
      .session()
      .then(applySession)
      .catch(() => applySession({ authenticated: false }))
      .finally(() => setLoading(false));
    return () => setUnauthorizedHandler(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      authenticated,
      username,
      async login(nextUser, password) {
        const session = await authApi.login(nextUser, password);
        applySession(session);
      },
      async logout() {
        try {
          await authApi.logout();
        } catch (error) {
          if (!(error instanceof ApiError)) {
            throw error;
          }
        }
        applySession({ authenticated: false });
      },
    }),
    [authenticated, loading, username],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
